import {
    calculatePageNumber,
    calculatePageNumberByArrowType,
    filterEvents,
    filterEventsByCategory,
    filterEventsBySearchValue,
    getEndOfDayTimestamp,
    getParamsFromState,
    getStartOfCurrentDayTimestamp,
} from '../utils';
import { PAGINATION_ARROWS } from '../../../constants/App';
import { ALERT_HISTORY, CURRENT_PAGE, SELECTED_DATE_TIMESTAMP_IN_MS } from '../constants';
import { convertTimestampMsToSec } from 'services/utilityService';
import { IDS } from 'services/alertHistory';

jest.mock('services/utilityService', () => ({
    convertTimestampMsToSec: jest.fn(),
}));

describe('getEndOfDayTimestamp', () => {
    it('calculates the timestamp representing the end of the day', () => {
        const startOfDayTimestamp = 1708984800000;

        // Invoke the function to get the actual end of the day timestamp
        const endOfDayTimestamp = getEndOfDayTimestamp(startOfDayTimestamp);

        // Assert that the calculated end of the day timestamp matches the expected value
        expect(endOfDayTimestamp).toBe(1709071200000);
    });
});

describe('calculatePageNumberByArrowType', () => {
    it('should return currentPage - 1 for type "prev" if currentPage is not 0', () => {
        expect(calculatePageNumberByArrowType(PAGINATION_ARROWS.PREV, 5)).toBe(4);
    });

    it('should return currentPage for type "prev" if currentPage is 0', () => {
        expect(calculatePageNumberByArrowType(PAGINATION_ARROWS.PREV, 0)).toBe(0);
    });

    it('should return currentPage + 1 for type "next"', () => {
        expect(calculatePageNumberByArrowType(PAGINATION_ARROWS.NEXT, 5)).toBe(6);
    });

    it('should return currentPage for unknown type', () => {
        expect(calculatePageNumberByArrowType('invalidType', 5)).toBe(5);
    });
});

describe('calculatePageNumber', () => {
    it('should return pageNumber if it is provided', () => {
        const options = { pageType: 'next', pageNumber: 10 };
        const currentPage = 5;
        expect(calculatePageNumber(options, currentPage)).toBe(10);
    });

    it('should return currentPage - 1 for type "prev" if currentPage is not 0', () => {
        const options = { pageType: 'prev' };
        const currentPage = 5;
        expect(calculatePageNumber(options, currentPage)).toBe(4);
    });

    it('should return currentPage + 1 for type "next"', () => {
        const options = { pageType: 'next' };
        const currentPage = 5;
        expect(calculatePageNumber(options, currentPage)).toBe(6);
    });

    it('should return currentPage for unknown type', () => {
        const options = { pageType: 'invalidType' };
        const currentPage = 5;
        expect(calculatePageNumber(options, currentPage)).toBe(5);
    });
});
describe('getStartOfCurrentDayTimestamp', () => {
    it('should return the start timestamp of the current day', () => {
        const setHours = jest.fn();
        const getTime = jest.fn();
        const spy = jest.spyOn(global, 'Date').mockImplementation(() => ({ setHours, getTime }));
        // Expected start timestamp for the provided mock date
        const startTimestamp = 1708984800000;
        getTime.mockReturnValue(startTimestamp);

        expect(getStartOfCurrentDayTimestamp()).toBe(startTimestamp);
        expect(setHours).toBeCalledWith(0, 0, 0, 0);
        expect(getTime).toBeCalledTimes(1);

        // Restore the original Date object
        spy.mockRestore();
    });
});

describe('getParamsFromState', () => {
    it('should return parameters from the state and payload', () => {
        convertTimestampMsToSec.mockImplementation((timestamp) => timestamp / 1000);
        const state = {
            ezlo: {
                serial: '123456789',
            },
            ezlos: {
                items: [
                    {
                        serial: '123456789',
                        controller_uuid: 'controller123',
                    },
                ],
            },
            [ALERT_HISTORY]: {
                [SELECTED_DATE_TIMESTAMP_IN_MS]: 1643625600000,
                [CURRENT_PAGE]: 2,
            },
        };
        const payload = {
            pageType: 'next',
        };

        const expectedParams = {
            timestamp_start: 1643625600,
            timestamp_end: 1643712000,
            list: [{ source: 'controller123' }],
            page: 3,
        };

        expect(getParamsFromState(state, payload)).toEqual(expectedParams);
        expect(convertTimestampMsToSec).toBeCalledTimes(2);
    });
});
describe('filterEventsByCategory', () => {
    it('should return the original event list when selected category ID is ALL_ACTIONS', () => {
        const eventList = [
            { id: 1, uiData: { categoryId: IDS.LIGHTS_AND_SWITCHES } },
            { id: 2, uiData: { categoryId: IDS.SYSTEM } },
        ];
        const selectedCategoryId = IDS.ALL_ACTIONS;
        expect(filterEventsByCategory(eventList, selectedCategoryId)).toEqual(eventList);
    });

    it('should return filtered event list based on the selected category ID', () => {
        const eventList = [
            { id: 1, uiData: { categoryId: IDS.LIGHTS_AND_SWITCHES } },
            { id: 2, uiData: { categoryId: IDS.SYSTEM } },
            { id: 3, uiData: { categoryId: IDS.LIGHTS_AND_SWITCHES } },
        ];
        const selectedCategoryId = IDS.LIGHTS_AND_SWITCHES;
        const expectedFilteredEvents = [
            { id: 1, uiData: { categoryId: IDS.LIGHTS_AND_SWITCHES } },
            { id: 3, uiData: { categoryId: IDS.LIGHTS_AND_SWITCHES } },
        ];
        expect(filterEventsByCategory(eventList, selectedCategoryId)).toEqual(expectedFilteredEvents);
    });

    it('should return an empty list when event list is empty', () => {
        const eventList = [];
        const selectedCategoryId = IDS.SYSTEM;
        expect(filterEventsByCategory(eventList, selectedCategoryId)).toEqual([]);
    });
});
describe('filterEventsBySearchValue', () => {
    it('should return the original event list when search value is empty', () => {
        const eventList = [
            { id: 1, uiData: { description: 'Event 1', time: '10:00 AM' } },
            { id: 2, uiData: { description: 'Event 2', time: '11:00 AM' } },
        ];
        const searchValue = '';
        expect(filterEventsBySearchValue(eventList, searchValue)).toEqual(eventList);
    });

    it('should return filtered event list based on the search value', () => {
        const eventList = [
            { id: 1, uiData: { description: 'Event 1', time: '10:00 AM' } },
            { id: 2, uiData: { description: 'Another one', time: '11:00 AM' } },
            { id: 3, uiData: { description: 'event', time: '12:00 PM' } },
        ];
        const searchValue = 'event';
        const expectedFilteredEvents = [
            { id: 1, uiData: { description: 'Event 1', time: '10:00 AM' } },
            { id: 3, uiData: { description: 'event', time: '12:00 PM' } },
        ];
        expect(filterEventsBySearchValue(eventList, searchValue)).toEqual(expectedFilteredEvents);
    });

    it('should return an empty list when search value does not match any event', () => {
        const eventList = [
            { id: 1, uiData: { description: 'Event 1', time: '10:00 AM' } },
            { id: 2, uiData: { description: 'Event 2', time: '11:00 AM' } },
        ];
        const searchValue = 'notfound';
        expect(filterEventsBySearchValue(eventList, searchValue)).toEqual([]);
    });
});

describe('filterEvents', () => {
    it('should return the original event list when no category and search value are provided', () => {
        const eventList = [
            { id: 1, uiData: { categoryId: 'someCategory', description: 'Event 1', time: '10:00 AM' } },
            { id: 2, uiData: { categoryId: 'someCategory', description: 'Event 2', time: '11:00 AM' } },
        ];
        const selectedCategoryId = IDS.ALL_ACTIONS;
        const searchValue = '';
        expect(filterEvents(eventList, selectedCategoryId, searchValue)).toEqual(eventList);
    });

    it('should return filtered event list based on the selected category ID', () => {
        const eventList = [
            { id: 1, uiData: { categoryId: IDS.SECURITY_DEVICES, description: 'Event 1', time: '10:00 AM' } },
            { id: 2, uiData: { categoryId: IDS.SYSTEM, description: 'Event 2', time: '11:00 AM' } },
            { id: 3, uiData: { categoryId: IDS.ALARM, description: 'Alarm triggered', time: '12:00 PM' } },
        ];
        const selectedCategoryId = IDS.SECURITY_DEVICES;
        const searchValue = '';
        const expectedFilteredEvents = [
            { id: 1, uiData: { categoryId: IDS.SECURITY_DEVICES, description: 'Event 1', time: '10:00 AM' } },
        ];
        expect(filterEvents(eventList, selectedCategoryId, searchValue)).toEqual(expectedFilteredEvents);
    });

    it('should return filtered event list based on the search value', () => {
        const eventList = [
            { id: 1, uiData: { categoryId: 'someCategory', description: 'Event 1', time: '10:00 AM' } },
            { id: 2, uiData: { categoryId: 'someCategory', description: 'Event 2', time: '11:00 AM' } },
            { id: 3, uiData: { categoryId: 'someCategory', description: 'Another', time: '12:00 PM' } },
        ];
        const selectedCategoryId = IDS.ALL_ACTIONS;
        const searchValue = 'event';
        const expectedFilteredEvents = [
            { id: 1, uiData: { categoryId: 'someCategory', description: 'Event 1', time: '10:00 AM' } },
            { id: 2, uiData: { categoryId: 'someCategory', description: 'Event 2', time: '11:00 AM' } },
        ];
        expect(filterEvents(eventList, selectedCategoryId, searchValue)).toEqual(expectedFilteredEvents);
    });

    it('should return empty list when no events match the selected category and search value', () => {
        const eventList = [
            {
                id: 1,
                uiData: { categoryId: IDS.LIGHTS_AND_SWITCHES, description: 'Light turned on', time: '10:00 AM' },
            },
            {
                id: 2,
                uiData: { categoryId: IDS.LIGHTS_AND_SWITCHES, description: 'Light turned off', time: '11:00 AM' },
            },
        ];
        const selectedCategoryId = IDS.SYSTEM;
        const searchValue = 'notfound';
        expect(filterEvents(eventList, selectedCategoryId, searchValue)).toEqual([]);
    });
});
